import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { PCrmServiceCategory } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Service Categories',
    description: 'Manage service categories',
    permissionModuleKey: 'service_category',
    route: {
      list: '/p_crm_service_category',
      new: '/p_crm_service_category/new',
      detail: (id: number) => `/p_crm_service_category/${id}`,
      edit: (id: number) => `/p_crm_service_category/${id}/edit`,
    },
    api: {
      list: '/p-crm-service-category',
      detail: (id: number) => `/p-crm-service-category/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search service categories...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['psc_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'psc_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: PCrmServiceCategory) => item.psc_descr,
        options: [],
      },
    ],
    applyFilter: (items: PCrmServiceCategory[], filters: { psc_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.psc_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.psc_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.psc_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Service Categorie',
        showTitle: false,
        variant: 'primary' as const,
        href: '/p_crm_service_category/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: PCrmServiceCategory) => {
      window.location.href = `/p_crm_service_category/${item.id}`;
    },
    keyExtractor: (item: PCrmServiceCategory) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new service categorie.',
    noSearchResults: 'No service categories found matching your search criteria.',
    noFilterResults: 'No service categories match the selected filters.',
  },
  delete: {
    title: 'Delete Service Categorie',
    message: 'Are you sure you want to delete this service categorie? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete service categorie. Please try again.',
    handler: async (id: number) => {
      const { deleteServiceCategory } = await import('../../../services/mutations');
      await deleteServiceCategory(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Service Categorie Details',
    notFoundTitle: 'Service Categorie not found',
    notFoundMessage: 'The service categorie you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Service Categories',
    defaultTitle: 'Unnamed Service Categorie',
    getTitle: (item: PCrmServiceCategory) => {
      return item.psc_descr || `Service Category #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Service Categorie',
      subtitle: 'Add a new service categorie',
      submitText: 'Create Service Categorie',
    },
    edit: {
      title: 'Edit Service Categorie',
      subtitle: 'Update service categorie information',
      submitText: 'Update Service Categorie',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading service categorie...',
    errorMessage: 'Failed to save service categorie. Please try again.',
    create: async (data: Partial<PCrmServiceCategory>) => {
      const { createServiceCategory } = await import('../../../services/mutations');
      return await createServiceCategory(data);
    },
    update: async (id: number, data: Partial<PCrmServiceCategory>) => {
      const { updateServiceCategory } = await import('../../../services/mutations');
      return await updateServiceCategory(id, data);
    },
  },
  fields: [
    {
      name: 'psc_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter service categorie description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Service Categorie Information',
        icon: '📝',
        fields: ['psc_descr'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<PCrmServiceCategory>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'psc_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: PCrmServiceCategory,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.psc_descr || '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: PCrmServiceCategory) => {
  return null;
};

export const getInitialFormData = (): Partial<PCrmServiceCategory> => {
  return {
    psc_descr: '',
  };
};

export const mapItemToFormData = (item: PCrmServiceCategory): Partial<PCrmServiceCategory> => {
  return {
    psc_descr: item.psc_descr || '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: PCrmServiceCategory,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

